/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  ScrollText, Gift, FileText, Home, Building2, Users, Layers, Split, Mail, ArrowRight,
  Sparkles, Globe2, MessageCircle, Compass, Coins, FileSignature,
} from 'lucide-react';

import SEO from '@/components/SEO';
import LibraryPageLayout from '@/components/library/LibraryPageLayout';
import LibraryStructuredData from '@/components/library/LibraryStructuredData';
import {
  LibraryHero,
  SectionHeader,
  LibraryDocumentCard,
  FeatureCheckItem,
  LibraryFAQ,
  LibraryFinalCta,
} from '@/components/library/sections';

/* ------------------------------------------------------------------ */
/*  Data — kept at the top of the page so future SEO pages can copy   */
/*  the same shape and only change content.                            */
/* ------------------------------------------------------------------ */

const HERO_TAGS = [
  { label: 'Acte de notoriété', to: '/acte-notoriete#definition' },
  { label: 'Déclaration de succession', to: '/declaration-succession#definition' },
  { label: 'Donation-partage', to: '/donation-partage#definition' },
  { label: 'Attestation immobilière', to: '/attestation-immobiliere#definition' },
  { label: 'Testament', to: '/testament#definition' },
  { label: 'SCI', to: '/sci#definition' },
  { label: 'Indivision', to: '/indivision#definition' },
  { label: 'Acte de vente', to: '/acte-de-vente#definition' },
  { label: 'Courrier notarial', to: '/courrier-notarial#definition' },
];

const DOC_CARDS = [
  {
    Icon: FileSignature,
    title: 'Acte de notoriété',
    description: 'Le document qui établit officiellement la qualité d\'héritier. HÉRITA précise qui est désigné et ce que cela permet de faire.',
    to: '/acte-notoriete',
  },
  {
    Icon: ScrollText,
    title: 'Déclaration de succession',
    description: 'Le document fiscal qui détaille les héritiers, l\'actif, le passif et les droits dus. HÉRITA en extrait les chiffres clés et les démarches.',
    to: '/declaration-succession',
  },
  {
    Icon: Gift,
    title: 'Donation-partage',
    description: 'Un acte qui transmet et partage de votre vivant. HÉRITA explique qui reçoit quoi, avec quels droits et quelles conséquences fiscales.',
    to: '/donation-partage',
  },
  {
    Icon: Home,
    title: 'Attestation immobilière',
    description: 'L\'acte qui constate le transfert d\'un bien immobilier après un décès. Quels biens, quelle quote-part, quelle valeur retenue.',
    to: '/attestation-immobiliere',
  },
  {
    Icon: FileText,
    title: 'Testament',
    description: 'Olographe ou authentique. HÉRITA explique les volontés exprimées, les legs, les bénéficiaires et les éventuels conflits avec la réserve.',
    to: '/testament',
  },
  {
    Icon: Layers,
    title: 'SCI',
    description: 'Statuts, cession de parts, assemblée générale. HÉRITA traduit les clauses essentielles et l\'organisation du patrimoine immobilier.',
    to: '/sci',
  },
  {
    Icon: Split,
    title: 'Indivision',
    description: 'Convention, sortie ou gestion d\'indivision. Qui décide, qui paie, comment sortir : les règles expliquées simplement.',
    to: '/indivision',
  },
  {
    Icon: Building2,
    title: 'Acte de vente',
    description: 'Acte authentique, compromis, promesse. HÉRITA met en lumière le prix, les conditions, les servitudes et les obligations de chacun.',
    to: '/acte-de-vente',
  },
  {
    Icon: Mail,
    title: 'Courrier notarial',
    description: 'Lettre, demande de pièces, projet d\'acte. HÉRITA repère ce qu\'on vous demande exactement et les délais à respecter.',
    to: '/courrier-notarial',
  },
];

const HERITA_AUTOMATIC_CHECKS = [
  'Personnes mentionnées (vendeurs, acheteurs, héritiers, légataires…)',
  'Biens immobiliers et mobiliers identifiés',
  'Montants, prix, valeurs retenues et soultes',
  'Clauses importantes du document',
  'Démembrement : usufruit et nue-propriété',
  'Démarches à effectuer après la signature',
  'Conséquences pratiques pour vous',
  'Calendrier et délais à respecter',
  'Questions à poser à votre notaire',
];

const WHY_CARDS = [
  {
    Icon: Sparkles,
    title: 'Comprendre plus vite',
    description: 'Plus besoin de relire dix fois un acte de quarante pages. HÉRITA vous donne l\'essentiel en quelques minutes.',
  },
  {
    Icon: Globe2,
    title: 'Langage clair',
    description: 'Le vocabulaire juridique est reformulé en français simple, sans approximation et sans jargon inutile.',
  },
  {
    Icon: MessageCircle,
    title: 'Préparer son rendez-vous chez le notaire',
    description: 'HÉRITA suggère les bonnes questions à poser, pour transformer votre rendez-vous en conversation utile.',
  },
  {
    Icon: Compass,
    title: 'Identifier ce qui vous concerne',
    description: 'À partir du document, HÉRITA met en avant ce qui vous touche personnellement : droits, montants, démarches.',
  },
];

const FAQ_ITEMS = [
  {
    q: 'Qu\'est-ce qu\'un document notarié ?',
    a: 'Un document notarié est un acte écrit, daté et signé par un notaire — officier public — qui lui confère une valeur juridique particulière. Acte de vente, donation-partage, testament authentique, attestation immobilière, déclaration de succession, statuts de SCI : tous appartiennent à cette famille. Leur point commun est d\'engager durablement votre patrimoine, vos droits ou ceux de vos proches.',
  },
  {
    q: 'Quels documents HÉRITA peut-il analyser ?',
    a: 'HÉRITA prend en charge l\'essentiel des documents notariés et patrimoniaux français : succession, donation-partage, testament, attestation immobilière, acte de notoriété, vente immobilière, indivision, SCI, courrier de notaire et autres actes notariés. Les formats acceptés sont PDF, JPG, PNG et WEBP.',
  },
  {
    q: 'HÉRITA remplace-t-il un notaire ?',
    a: 'Non. HÉRITA ne remplace pas votre notaire. Il vous aide à comprendre, en langage clair, ce que votre document contient et ce qu\'il implique. Le notaire reste l\'interlocuteur officiel pour interpréter juridiquement l\'acte, valider votre situation personnelle et accomplir les formalités.',
  },
  {
    q: 'Les documents sont-ils conservés ?',
    a: 'Vos documents originaux sont supprimés après l\'analyse. Seul le résultat — la lecture personnalisée que vous avez débloquée — reste accessible via votre lien permanent. À aucun moment HÉRITA ne partage vos documents avec un tiers.',
  },
  {
    q: 'Combien coûte l\'analyse ?',
    a: 'L\'aperçu initial est gratuit. La lecture complète personnalisée coûte 19 € en paiement unique — sans abonnement, sans engagement. Vous ne payez qu\'une seule fois pour un document : vous pouvez ensuite y revenir quand vous voulez via votre lien permanent.',
  },
  {
    q: 'Quels actes sont compatibles ?',
    a: 'Tous les actes notariés français au format numérique : succession, donation-partage, testament, attestation immobilière, vente immobilière, partage, indivision, SCI, courrier notarial et autres documents notariés. Si votre document est lisible, HÉRITA peut généralement l\'analyser.',
  },
  {
    q: 'Puis-je analyser plusieurs documents ?',
    a: 'Oui. Chaque document est analysé séparément et donne lieu à son propre dossier, son propre aperçu gratuit et, si vous le souhaitez, sa propre lecture complète à 19 €. Vous pouvez donc utiliser HÉRITA autant de fois que nécessaire.',
  },
  {
    q: 'Mes données sont-elles confidentielles ?',
    a: 'Oui. HÉRITA respecte le RGPD. Vos documents originaux ne sont jamais partagés et sont supprimés après l\'analyse. Les noms, montants et adresses présents dans votre acte ne sont utilisés que pour produire votre lecture personnalisée. Pour plus de détails, consultez notre page Confidentialité & sécurité.',
  },
  {
    q: 'Combien de temps dure l\'analyse ?',
    a: 'L\'analyse complète prend généralement entre 30 secondes et quelques minutes, selon la taille de votre document. Pour les actes notariés volumineux (vente immobilière avec annexes, succession complexe), comptez deux à trois minutes au maximum.',
  },
  {
    q: 'Comment fonctionne l\'intelligence artificielle ?',
    a: 'HÉRITA utilise des modèles d\'intelligence artificielle français à l\'état de l\'art. L\'IA lit votre document, identifie les personnes, les biens, les montants et les clauses importantes, puis les reformule en langage clair en s\'appuyant sur le vocabulaire notarial français. L\'objectif n\'est pas de remplacer le notaire, mais de vous rendre l\'acte enfin lisible.',
  },
];

const BREADCRUMB = [
  { label: 'Accueil', to: '/' },
  { label: 'Guides', to: '/comprendre-document-notarie' },
  { label: 'Comprendre les documents notariés' },
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const SEO_TITLE = 'Guides des documents notariés | HÉRITA';
const SEO_DESCRIPTION =
  "Découvrez les principaux documents notariés français, leur rôle, leur contenu et leurs conséquences. HÉRITA vous aide à comprendre simplement les actes envoyés par votre notaire.";

const UnderstandNotarialDocument = () => {
  return (
    <>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        path="/comprendre-document-notarie"
        ogTitle="Comprendre les documents notariés — Guides HÉRITA"
        ogDescription={SEO_DESCRIPTION}
      />
      <LibraryStructuredData
        path="/comprendre-document-notarie"
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        breadcrumb={BREADCRUMB}
        faq={FAQ_ITEMS}
        datePublished="2026-02-18"
        dateModified="2026-02-18"
      />

      <LibraryPageLayout
        breadcrumb={BREADCRUMB}
        testId="page-comprendre-document-notarie"
      >
        {/* ───────────── HERO ───────────── */}
        <LibraryHero
          eyebrow="Guides HÉRITA"
          title={(
            <>
              Comprendre <br className="hidden sm:block" />les documents notariés
            </>
          )}
          subtitle="Acte de notoriété, déclaration de succession, donation-partage, testament, attestation immobilière, SCI, indivision, vente, courrier… Chaque acte du notaire a sa logique propre. HÉRITA vous aide à comprendre simplement, dans votre langue, ce que chacun signifie et ce qu'il implique pour vous."
          trustItems={[
            'Analyse gratuite au départ',
            'Aucun compte requis',
            'Documents supprimés après analyse',
          ]}
          tags={HERO_TAGS}
        />

        {/* ───────── POURQUOI DIFFICILE ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF]"
          aria-labelledby="why-hard-heading"
        >
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Le constat"
              title={"Pourquoi les documents notariés sont-ils difficiles à comprendre\u00A0?"}
            />

            <div className="font-['Work_Sans'] text-[15.5px] sm:text-[16px] text-[#2C363F] leading-[1.85] space-y-6">
              <p>
                À la fin d'un rendez-vous chez le notaire, vous repartez souvent
                avec un dossier épais et une impression mêlée. Vous savez que ce
                document est important, qu'il engage votre patrimoine, votre
                famille ou votre logement. Mais quand vous le rouvrez chez vous,
                quelque chose résiste. Les phrases sont longues, les formules
                anciennes, et chaque mot semble avoir un poids particulier.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Un vocabulaire qui appartient à un autre temps.</strong>{' '}
                Les actes notariés français reprennent depuis plus de deux
                siècles une langue précise, héritée du Code civil. Des termes
                comme « nue-propriété », « quote-part indivise », « rapport
                successoral » ou « usufruit viager » ont un sens technique très
                exact — mais aucun équivalent dans la vie courante. Ils sont
                indispensables au notaire pour rédiger sans ambiguïté, mais
                opaques pour la plupart des lecteurs.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Des clauses qui se répondent les unes aux autres.</strong>{' '}
                Un acte notarié n'est pas un texte qui se lit en ligne droite.
                Il fonctionne par renvois&nbsp;: une définition au début, une
                condition au milieu, une conséquence à la fin. Pour comprendre
                ce que cela change pour vous, il faut souvent assembler trois
                ou quatre paragraphes dispersés dans le document. C'est un
                exercice exigeant, et c'est précisément ce que votre notaire
                fait pour vous quand il vous reçoit.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Des conséquences patrimoniales bien réelles.</strong>{' '}
                Au-delà du vocabulaire, ces documents engagent souvent des
                années de votre vie&nbsp;: un héritage à recevoir, un bien à
                vendre, des parts d'une SCI à céder, une donation à intégrer
                dans la succession future. Une ligne mal lue peut signifier une
                soulte à verser, un droit à payer ou un usufruit qui s'éteint.
                On a donc envie — c'est légitime — de tout comprendre avant de
                signer ou de classer.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Une question qui revient toujours&nbsp;: « ce document, qu'est-ce qu'il change pour moi&nbsp;? ».</strong>{' '}
                C'est l'angle mort des actes notariés. Ils décrivent une
                réalité juridique commune à toutes les parties — vendeur,
                acheteur, héritiers, donateur — sans jamais s'adresser
                directement à vous. Or chacun y a un rôle différent, des
                droits différents, des obligations différentes. C'est pour
                cette raison que tant de gens cherchent en ligne « comment lire
                une donation-partage » ou « comprendre un acte de succession ».
              </p>

              <p>
                HÉRITA part de cette frustration et la traite à la racine. Vous
                importez votre document, l'intelligence artificielle identifie
                les éléments importants — les personnes, les biens, les
                montants, les clauses — puis elle les réécrit en français
                clair, dans l'ordre où ils vous concernent. Pas pour remplacer
                votre notaire, mais pour vous permettre d'arriver à votre
                rendez-vous en sachant déjà ce que dit votre document, et avec
                les bonnes questions en tête.
              </p>
            </div>

            <h2 id="why-hard-heading" className="sr-only">
              Pourquoi les documents notariés sont-ils difficiles à comprendre ?
            </h2>
          </div>
        </section>

        {/* ───────── DOCS GRID ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20"
          aria-labelledby="docs-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Guides"
              title="Les principaux documents notariés"
              intro="Chaque document a sa logique propre. Choisissez le vôtre pour découvrir comment HÉRITA l'explique en détail."
            />
            <h2 id="docs-heading" className="sr-only">Documents notariés expliqués</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {DOC_CARDS.map((card) => (
                <LibraryDocumentCard key={card.title} {...card} />
              ))}
            </div>
          </div>
        </section>

        {/* ───────── HERITA ANALYSE ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF]"
          aria-labelledby="auto-heading"
        >
          <div className="max-w-5xl mx-auto">
            <SectionHeader
              eyebrow="Sans rien demander"
              title="Ce que HÉRITA analyse automatiquement"
              intro="Dès que votre document est importé, HÉRITA en extrait ce qui compte pour vous, sans formulaire ni paramétrage."
            />
            <h2 id="auto-heading" className="sr-only">Ce que HÉRITA analyse</h2>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0">
              {HERITA_AUTOMATIC_CHECKS.map((label) => (
                <FeatureCheckItem key={label} label={label} />
              ))}
            </ul>
          </div>
        </section>

        {/* ───────── TIMELINE ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20"
          aria-labelledby="how-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Méthode"
              title={"Comment fonctionne HÉRITA\u00A0?"}
            />
            <h2 id="how-heading" className="sr-only">Comment fonctionne HÉRITA</h2>

            <ol className="relative space-y-10 sm:space-y-14 list-none p-0">
              {[
                {
                  n: '01',
                  title: 'Importez votre document',
                  text: 'Glissez-déposez votre acte notarié au format PDF, JPG ou PNG. Aucun compte n\'est requis. Le document reste privé et est supprimé après l\'analyse.',
                },
                {
                  n: '02',
                  title: 'Notre IA identifie les informations importantes',
                  text: 'HÉRITA lit l\'acte de bout en bout, repère les personnes, les biens, les montants, les clauses sensibles et les démarches associées.',
                },
                {
                  n: '03',
                  title: 'Vous recevez une explication claire et personnalisée',
                  text: 'En quelques minutes, vous obtenez une lecture structurée, en français simple, des conséquences concrètes pour vous et des bonnes questions à poser à votre notaire.',
                },
              ].map((step, i, arr) => (
                <li key={step.n} className="relative flex items-start gap-5 sm:gap-7">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#1A2530] text-white flex items-center justify-center font-['IBM_Plex_Mono'] text-[13px] tracking-wider">
                      {step.n}
                    </div>
                    {i < arr.length - 1 && (
                      <div
                        aria-hidden="true"
                        className="absolute left-1/2 -translate-x-1/2 top-12 sm:top-14 w-px bg-[#D3CCBE]"
                        style={{ height: 'calc(100% + 32px)' }}
                      />
                    )}
                  </div>
                  <div className="pt-2 sm:pt-3">
                    <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-2 leading-tight">
                      {step.title}
                    </h3>
                    <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ───────── EXEMPLE ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF]"
          aria-labelledby="example-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Avant · Après"
              title="Un extrait notarial, traduit en clair"
              intro="À gauche, un extrait fictif tel qu'on en trouve dans une donation-partage. À droite, ce qu'HÉRITA en aurait expliqué."
            />
            <h2 id="example-heading" className="sr-only">Exemple avant et après</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              {/* Left — fictional extract */}
              <article className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-7 sm:p-9 font-['Cormorant_Garamond'] text-[#1A2530]">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-4">
                  Extrait du document (fictif)
                </p>
                <p className="text-[16px] sm:text-[17px] leading-[1.85] italic">
                  « Sera attribué à Madame Léa MARTIN, à titre de partage
                  anticipé entre ses enfants nés et à naître, la nue-propriété
                  de l'immeuble sis [Adresse], évalué à la somme de DEUX CENT
                  QUATRE-VINGT-QUATRE MILLE DEUX CENT QUATRE-VINGT-DEUX EUROS
                  (284&nbsp;282&nbsp;€), à charge pour elle de respecter les
                  droits d'usufruit réservés par le donateur, sa vie durant,
                  conformément aux dispositions des articles 595 et suivants du
                  Code civil. »
                </p>
              </article>

              {/* Right — HÉRITA explanation */}
              <article className="bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-7 sm:p-9 shadow-md">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#1A2530] mb-4">
                  Lecture HÉRITA
                </p>
                <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-5 leading-tight">
                  Ce que cela signifie pour Léa
                </h3>

                <ul className="space-y-3.5 list-none p-0">
                  {[
                    { strong: 'Vous recevez la nue-propriété', rest: ' d\'un immeuble évalué à 284 282 €.' },
                    { strong: 'Vous n\'êtes pas propriétaire à 100 %', rest: ' tout de suite : vos parents conservent l\'usufruit jusqu\'à leur décès.' },
                    { strong: 'Concrètement', rest: ', vos parents peuvent continuer à habiter le bien ou en percevoir les loyers.' },
                    { strong: 'À leur décès', rest: ', vous deviendrez pleinement propriétaire, sans nouveau droit à payer.' },
                    { strong: 'Cette attribution', rest: ' s\'imputera sur votre part de la succession à venir.' },
                  ].map((line, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1A2530] flex-shrink-0" />
                      <span className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#1A2530] leading-snug">
                        <strong className="font-semibold">{line.strong}</strong>{line.rest}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-6 pt-5 border-t border-[#E2DBCF] font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 flex items-center gap-2">
                  <Coins className="h-3 w-3" aria-hidden="true" />
                  Lecture réalisée en moins de 30 secondes
                </p>
              </article>
            </div>

            <p className="mt-8 font-['Work_Sans'] text-[12.5px] text-[#2C363F] italic text-center opacity-80">
              Exemple illustratif. Le texte d'origine est inventé pour montrer
              le rendu d'HÉRITA — il ne correspond à aucun document réel.
            </p>
          </div>
        </section>

        {/* ───────── POURQUOI HÉRITA ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20"
          aria-labelledby="why-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Bénéfices"
              title={"Pourquoi utiliser HÉRITA\u00A0?"}
            />
            <h2 id="why-heading" className="sr-only">Pourquoi utiliser HÉRITA</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {WHY_CARDS.map(({ Icon, title, description }) => (
                <article
                  key={title}
                  className="bg-white border border-[#E2DBCF] rounded-2xl p-7 sm:p-8 hover:shadow-md transition-shadow"
                >
                  <div className="w-11 h-11 rounded-full bg-[#E3D5CA] flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#1A2530]" aria-hidden="true" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[24px] text-[#1A2530] mb-2 leading-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                    {description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ───────── FAQ ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF]"
          aria-labelledby="faq-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Questions fréquentes"
              title="Tout ce que vous voulez savoir"
              intro="Dix questions concrètes pour vous aider à comprendre comment HÉRITA fonctionne, ce qu'il fait, et ce qu'il ne fait pas."
            />
            <h2 id="faq-heading" className="sr-only">Questions fréquentes</h2>

            <LibraryFAQ items={FAQ_ITEMS} defaultOpen={0} />
          </div>
        </section>

        {/* ───────── FINAL CTA ───────── */}
        <LibraryFinalCta
          title={"Vous avez reçu un document du notaire\u00A0?"}
          subtitle="Déposez-le en quelques secondes. HÉRITA identifie automatiquement les personnes, les biens, les montants et les conséquences importantes, puis vous explique ce que votre document signifie réellement."
        />

        {/* ───────── DISCOVER THE LIBRARY ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-t border-[#E2DBCF]"
          aria-labelledby="library-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Aller plus loin"
              title="Explorer les Guides HÉRITA"
              intro="Chaque document mérite sa propre lecture. Découvrez nos guides dédiés à chaque acte notarié."
            />
            <h2 id="library-heading" className="sr-only">Guides HÉRITA</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { label: 'Acte de notoriété', to: '/acte-notoriete' },
                { label: 'Déclaration de succession', to: '/declaration-succession' },
                { label: 'Donation-partage', to: '/donation-partage' },
                { label: 'Attestation immobilière', to: '/attestation-immobiliere' },
                { label: 'Testament', to: '/testament' },
                { label: 'SCI', to: '/sci' },
                { label: 'Indivision', to: '/indivision' },
                { label: 'Acte de vente', to: '/acte-de-vente' },
              ].map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`library-discover-${to}`}
                  className="group block bg-[#F9F7F3] border border-[#E2DBCF] rounded-xl p-5 hover:border-[#1A2530] hover:bg-white transition-all"
                >
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-2">
                    Comprendre
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-[20px] sm:text-[22px] text-[#1A2530] leading-tight mb-3 font-medium">
                    {label}
                  </p>
                  <span className="inline-flex items-center font-['Work_Sans'] text-[12.5px] font-medium text-[#1A2530] group-hover:underline underline-offset-[5px] decoration-[#1A2530]">
                    Lire le guide
                    <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Internal cross-link icon (kept for SEO; visually neutral) */}
        <Users aria-hidden="true" className="sr-only" />
      </LibraryPageLayout>
    </>
  );
};

export default UnderstandNotarialDocument;
